import { logger, createLogger } from '@/utils/logger'

// Create a namespaced logger
const fileLogger = createLogger('http')

import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import type { Router } from 'vue-router'
import { JsonApiTransformer } from './jsonapi'
import { IApiError, type IApiErrorDetail } from '@/types'
import type { LoadingStore } from '@/stores/loading'
import { StorageService } from '@/services'
import { getConfig } from '@/config'
import { createNetworkError, isNetworkError } from './networkErrorHandler'

type RetryAxiosConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
  skipAuthRefresh?: boolean
}

export class Http {
  protected instance: AxiosInstance
  protected router?: Router
  protected transformer: JsonApiTransformer
  protected loadingStore?: LoadingStore
  protected storage: StorageService
  /** Single-flight refresh so concurrent 401s share one refresh call */
  private refreshPromise: Promise<boolean> | null = null

  constructor() {
    this.storage = new StorageService()
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'Accept': 'application/vnd.api+json'
      },
      timeout: 30000,
    })

    this.transformer = new JsonApiTransformer()
    this.setupInterceptors()
  }

  setRouter(router: Router) {
    this.router = router
  }

  setLoadingStore(store: LoadingStore) {
    this.loadingStore = store
  }

  async setToken(token: string | null) {
    fileLogger.log('Setting auth token:', token ? 'Token provided' : 'Clearing token')
    if (token) {
      this.instance.defaults.headers.common['Authorization'] = `Bearer ${token}`
      await this.storage.set('auth_token', token)
      fileLogger.log('Auth token stored successfully in instance and localStorage')
    } else {
      delete this.instance.defaults.headers.common['Authorization']
      await this.storage.remove('auth_token')
      await this.storage.remove('refresh_token')
      fileLogger.log('Auth token removed from instance and localStorage')
    }
  }

  async setRefreshToken(refreshToken: string | null) {
    if (refreshToken) {
      await this.storage.set('refresh_token', refreshToken)
      fileLogger.log('Refresh token stored')
    } else {
      await this.storage.remove('refresh_token')
      fileLogger.log('Refresh token removed')
    }
  }

  /**
   * Exchange refresh_token for a new access (+ rotated refresh) pair.
   * Returns true when tokens were updated and the original request may be retried.
   */
  async refreshAccessToken(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    this.refreshPromise = this.performRefresh()
    try {
      return await this.refreshPromise
    } finally {
      this.refreshPromise = null
    }
  }

  private async performRefresh(): Promise<boolean> {
    const refreshToken = await this.storage.get<string>('refresh_token')
    if (!refreshToken) {
      fileLogger.warn('No refresh token available; cannot renew session')
      return false
    }

    const config = getConfig()
    const baseUrl = (config.api.baseUrl || import.meta.env.VITE_API_URL || '').replace(/\/$/, '')
    const version = config.api.version || 'v1'
    const url = `${baseUrl}/${version}/auth/refresh`

    try {
      fileLogger.log('Refreshing access token')
      // Response interceptor unwraps to response.data
      const data = await this.instance.post<{
        access_token?: string
        refresh_token?: string
        status?: boolean | string
      }>(
        url,
        {
          refresh_token: refreshToken,
          app_id: config.app.contentId,
        },
        {
          skipAuthRefresh: true,
          headers: {
            Authorization: undefined,
          },
        } as AxiosRequestConfig,
      )

      if (!data?.access_token) {
        fileLogger.warn('Refresh response missing access_token')
        return false
      }

      await this.setToken(data.access_token)
      if (data.refresh_token) {
        await this.setRefreshToken(data.refresh_token)
      }

      try {
        const { useAuthStore } = await import('@/stores/auth')
        useAuthStore().token = data.access_token
      } catch (storeError) {
        fileLogger.warn('Could not sync auth store after refresh:', storeError)
      }

      fileLogger.log('Access token refreshed successfully')
      return true
    } catch (error) {
      fileLogger.error('Failed to refresh access token:', error)
      return false
    }
  }

  private isAuthCredentialRequest(url?: string): boolean {
    if (!url) return false
    return (
      url.includes('/login') ||
      url.includes('/register') ||
      url.includes('/password-reset') ||
      url.includes('/password-forgot') ||
      url.includes('/auth/refresh')
    )
  }

  private async invalidateSessionAndRedirect(hadAuthorization: boolean): Promise<void> {
    if (hadAuthorization) {
      try {
        const { useAuthStore } = await import('@/stores/auth')
        await useAuthStore().logout({ skipSync: true, reason: 'session_invalid' })
      } catch (clearError) {
        fileLogger.error('Failed to clear session on 401:', clearError)
        await this.setToken(null)
      }
    } else {
      fileLogger.warn('401 without Authorization header; keeping existing session')
    }

    const currentRoute = this.router?.currentRoute.value

    if (
      hadAuthorization &&
      this.router &&
      currentRoute &&
      !['/login', '/register', '/forgot-password', '/reset-password'].some(
        path => currentRoute.path.startsWith(path)
      )
    ) {
      localStorage.setItem('redirectAfterLogin', currentRoute.fullPath)
      this.router.push({ name: 'login' })
    }
  }

  protected setupInterceptors() {
    this.instance.interceptors.request.use(
      async (config: RetryAxiosConfig) => {
        if (config.showGlobalLoading) {
          this.loadingStore?.startLoading()
        }

        // Refresh must not re-attach a stale Bearer token
        if (config.skipAuthRefresh) {
          delete config.headers.Authorization
          delete config.headers.authorization
          return config
        }

        // Fast path: Check if token exists in instance headers first
        if (config.headers.Authorization) {
          return config
        }

        // Only check storage if we don't have the token in memory
        const token = await this.storage.get('auth_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        if (error.config?.showGlobalLoading) {
          this.loadingStore?.stopLoading()
        }
        return Promise.reject(error)
      }
    )

    this.instance.interceptors.response.use(
      (response) => {
        if (response.config?.showGlobalLoading) {
          this.loadingStore?.stopLoading()
        }
        if (response.data && (response.data.data || response.data.errors) && !response.data.success) {
          return this.transformer.transform(response.data)
        }
        return response.data
      },
      async (error) => {
        if (error.config?.showGlobalLoading) {
          this.loadingStore?.stopLoading()
        }

        // Handle network errors first
        if (isNetworkError(error) || !error.response) {
          const networkError = createNetworkError(error)
          fileLogger.error('Network error in HTTP interceptor:', networkError)
          return Promise.reject(networkError)
        }

        const originalConfig = error.config as RetryAxiosConfig | undefined

        if (error.response?.status === 401) {
          const isAuthRequest = this.isAuthCredentialRequest(originalConfig?.url)

          const authHeader =
            originalConfig?.headers?.Authorization ?? originalConfig?.headers?.authorization
          const hadAuthorization = Boolean(authHeader)

          if (isAuthRequest) {
            // Wrong password / invalid refresh — do not wipe an existing session via soft logout
            // for login/register; only clear tokens when this was a refresh attempt.
            if (originalConfig?.url?.includes('/auth/refresh') || originalConfig?.skipAuthRefresh) {
              // Refresh failed; caller (performRefresh) handles false return
            } else if (!hadAuthorization) {
              // Login/register 401 with no prior bearer — leave session alone
            }
          } else if (hadAuthorization && originalConfig && !originalConfig._retry) {
            const refreshed = await this.refreshAccessToken()
            if (refreshed) {
              originalConfig._retry = true
              originalConfig.headers = originalConfig.headers || {}
              const newToken = await this.storage.get<string>('auth_token')
              if (newToken) {
                originalConfig.headers.Authorization = `Bearer ${newToken}`
              }
              return this.instance.request(originalConfig)
            }

            await this.invalidateSessionAndRedirect(true)
            // Fall through to IApiError — do not return empty guest lists after wiping a real session
          } else if (hadAuthorization && originalConfig?._retry) {
            await this.invalidateSessionAndRedirect(true)
          } else if (!hadAuthorization) {
            fileLogger.warn('401 without Authorization header; keeping existing session')
            const config = getConfig()
            if (config.features.guestMode && !isAuthRequest) {
              return { data: [], meta: { current_page: 1, last_page: 1 } }
            }
          }
        }

        // Handle 404 errors for layout endpoints gracefully
        if (error.response?.status === 404) {
          const url = error.config?.url || ''
          const isLayoutEndpoint = url.includes('/settings/web-layout-')

          if (isLayoutEndpoint) {
            fileLogger.log(`Layout endpoint not found: ${url}, returning null for graceful fallback`)
            return null
          }
        }

        if (error.response?.data?.errors || error.response?.status === 401) {
          const errors = error.response.data?.errors || [{
            detail: error.response.data?.message ||
                   (error.response.status === 401 ? 'Authentication token is invalid or expired' : 'Unauthorized')
          }]

          const fieldErrors = this.parseErrors(errors)
          throw new IApiError(
            fieldErrors.general || fieldErrors.token || 'Request failed',
            fieldErrors,
            error.response.status
          )
        }

        return Promise.reject(error)
      }
    )
  }

  protected parseErrors(errors: IApiErrorDetail[]): Record<string, string> {
    return errors.reduce((acc: Record<string, string>, err: IApiErrorDetail) => {
      if (err.source?.pointer) {
        const field = err.source.pointer.split('/').pop() || 'general'
        acc[field] = err.detail || err.title || 'Unknown error'
      } else if (err.source?.parameter) {
        // Handle parameter-based errors (like token)
        acc[err.source.parameter] = err.detail || err.title || 'Unknown error'
      } else {
        acc.general = err.detail || err.title || 'Unknown error'
      }
      return acc
    }, {})
  }

  async get<T = any>(url: string, config?: any): Promise<T> {
    return this.instance.get(url, config)
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<T> {
    return this.instance.post(url, data, config)
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<T> {
    return this.instance.put(url, data, config)
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<T> {
    return this.instance.patch(url, data, config)
  }
}

export const http = new Http()
